import { TextAssist } from '../comments/TextAssist';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { V2Area, V2ChecklistItem } from '../../domain/inspection';
import { isInspectionLocked } from '../../domain/inspection';
import { translate, useT, type MessageKey, type MessageParams } from '../../i18n/i18n';
import { aiFailureMessage, tradeLabel } from '../../i18n/labels';
import { rowContextFor, rowReportSentence, storedSentenceCurrent, storedSentenceFor, unsyncedWordingLanguage, wordingBasisOf } from '../../report/rowWording';
import { defectFactsOfItem } from '../../report/defectFacts';
import { aiJobsStore } from '../../state/aiJobsStore';
import { aiPolicyBlockFor, aiStore, generateAreaReportWordingAction, rowReferralTrade, rowWordingFacts, wordingLiveOf } from '../../state/aiStore';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { inspectionStore, setItemReportSentenceByInspectorAction } from '../../state/inspectionStore';
import { Button, ConfirmSheet, InlineMessage, SectionEyebrow, TextArea, showToast } from '../../ui/components';
import { useAutosaveText, useOnlineStatus } from '../../ui/hooks';

// How the AI is asked to write the row's sentence (src/state/aiStore.ts WordingWriteOptions):
//   default          from everything the inspector left on the row (AI写这一项 / AI重写)
//   fromWording      the inspector WROTE the sentence — the AI renders those words and brings both
//                    languages in line (AI 按我写的重新生成)
//   fromObservation  the inspector asks for the sentence to be written from the row's AI observations
//                    (the button under ③, Owner device test 2026-09-21)
type WriteMode = 'default' | 'fromWording' | 'fromObservation';

// Whether an AI write can be offered on this row at all — shared by ⑥ and by the button under ③.
export function useWordingWriteAvailability(item: V2ChecklistItem) {
  const online = useOnlineStatus();
  const busy = useStore(aiStore, (s) => wordingLiveOf(s, item.areaId));
  const inspection = useStore(inspectionStore, (s) => s.inspections.find((i) => i.id === item.inspectionId) ?? null);
  const locked = inspection ? isInspectionLocked(inspection) : true;
  // PP-21 layer 1: the AI write is offered only where the policy allows a cloud call; the reason shows.
  const policyBlock = inspection ? aiPolicyBlockFor(inspection) : 'blockedRealClient';
  const recorded = item.status === 'checkedOk' || item.status === 'issueFound';
  return { online, busy, locked, policyBlock, recorded, available: recorded && !locked && !busy && online && policyBlock === null };
}

// ⑥ AI报告语 (Final Owner Decision 2026-09-14, CA-13 / CA-14 / CA-19; Owner device test 2026-09-21):
//   A  the sentence in the App Language FIRST, editable — a Chinese-speaking inspector reads and edits
//      Chinese (Owner: 「中文版报告语应该显示中文，英文在下面，这样验屋师更改的时候也可直接改成中文」);
//   B  the other language under it. In the Chinese app that is the formal English the client reads,
//      still editable; in the English app it is the Chinese reference, read-only as before;
//   C  the responsible professional (the frozen trade library; PE only on the inspector's structural fact);
//   D  the AI: AI写这一项 / AI重写, or — once the inspector wrote the sentence themselves — AI 按我写的重新生成,
//      which renders the inspector's words as report language and brings both languages in line.
// Whichever language the inspector wrote LAST stands; the other one is marked "not synced" until the AI
// or the inspector brings it in line. An English that is not in line with the inspector's Chinese also
// blocks the closeout (src/state/closeout.ts): the client must read what the inspector wrote.
// The sentence shown is the one the report prints: the stored sentence while it stands, else the
// library sentence (src/report/rowWording.ts). Nothing here calls the AI by itself.
export function ReportWordingBlock(props: { item: V2ChecklistItem; area: V2Area; testId?: string; observationWriteSignal?: number }) {
  const { t, locale } = useT();
  const aiState = useStore(aiStore, (s) => s);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const jobs = useStore(aiJobsStore, (s) => s.jobs);
  const item = props.item;
  const { online, busy, locked, policyBlock, recorded } = useWordingWriteAvailability(item);
  const [confirm, setConfirm] = useState<WriteMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The advanced review summary is part of the basis (the jobs list is read so the basis follows it).
  const facts = useMemo(() => rowWordingFacts(item, aiState, evidenceState), [item, aiState, evidenceState, jobs]);
  const basis = wordingBasisOf(item, facts);
  const trade = rowReferralTrade(item);
  const tEn = (key: MessageKey, params?: MessageParams) => translate('en-US', key, params);
  const tZh = (key: MessageKey, params?: MessageParams) => translate('zh-CN', key, params);
  const current = storedSentenceCurrent(item, basis);
  const storedEn = storedSentenceFor('en-US', item, basis);
  const storedZh = storedSentenceFor('zh-CN', item, basis);
  // the library sentence exactly as the report words it (2026-10-28: the row's place, what it may claim, the
  // AI's facts on a Defect row nobody worded yet — src/report/assembly.ts sentenceOf)
  const inspectionState = useStore(inspectionStore, (s) => s);
  const library = (lang: 'en-US' | 'zh-CN') => {
    const context = rowContextFor(lang, props.area, item, inspectionState.areas.filter((a) => a.inspectionId === item.inspectionId), inspectionState.items.filter((i) => i.inspectionId === item.inspectionId));
    return rowReportSentence(lang, item, context.areaName, { tradeLabel: trade ? tradeLabel(lang === 'en-US' ? tEn : tZh, trade) : null, ...context.options, defectFacts: defectFactsOfItem(aiState.observations, item.id, lang) });
  };
  const en = recorded ? (storedEn ?? library('en-US')) : '';
  const zh = recorded ? (storedZh ?? library('zh-CN')) : '';
  const source: 'ai' | 'inspector' | 'library' = current && item.reportSentence ? item.reportSentence.source : 'library';
  const unsynced = unsyncedWordingLanguage(item, basis);
  const factsChanged = source === 'inspector' && item.reportSentence !== null && item.reportSentence.basis !== basis;
  const zhFirst = locale === 'zh-CN';

  // The other language travels as it STANDS ('' when the sheet shows the library sentence there), so a
  // stale AI sentence is never carried forward under the inspector's name.
  const saveEn = useCallback((value: string) => setItemReportSentenceByInspectorAction(item.id, value, basis, Date.now(), 'en', storedZh ?? ''), [item.id, basis, storedZh]);
  const saveZh = useCallback((value: string) => setItemReportSentenceByInspectorAction(item.id, value, basis, Date.now(), 'zh', storedEn ?? ''), [item.id, basis, storedEn]);
  const enText = useAutosaveText(en, saveEn);
  const zhText = useAutosaveText(zh, saveZh);

  async function write(mode: WriteMode) {
    setError(null);
    // What the inspector just typed is saved first: "regenerate from what I wrote" must see it.
    await Promise.all([enText.flush(), zhText.flush()]);
    const outcome = await generateAreaReportWordingAction(item.areaId, { itemIds: [item.id], fromInspectorWording: mode === 'fromWording', adoptObservations: mode === 'fromObservation' });
    if (!outcome.ok) setError(aiFailureMessage(t, outcome.code));
    // the inspector typed into this row while the AI was writing: their words were kept
    else if (outcome.rows === 0 && outcome.kept) showToast(t('item.reportWordingKept'), 'info');
    else showToast(t('item.reportWordingWritten'));
  }

  // The inspector's own words are never replaced without asking first (CA-13) — whichever way the AI
  // is asked to write.
  async function requestWrite(requested: WriteMode) {
    await Promise.all([enText.flush(), zhText.flush()]);
    // Read AFTER the flush: text typed a moment ago has just become the inspector's sentence, and a
    // plain "write" pressed right after typing means "write it from what I typed".
    const authored = inspectionStore.get().items.find((i) => i.id === item.id)?.reportSentence?.source === 'inspector';
    const mode: WriteMode = authored && requested === 'default' ? 'fromWording' : requested;
    if (authored) setConfirm(mode);
    else void write(mode);
  }

  // The button under ③ (用 AI 观察生成报告语) asks through the same door.
  const lastSignal = useRef(props.observationWriteSignal ?? 0);
  useEffect(() => {
    const signal = props.observationWriteSignal ?? 0;
    if (signal === lastSignal.current) return;
    lastSignal.current = signal;
    void requestWrite('fromObservation');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fired by the signal only
  }, [props.observationWriteSignal]);

  if (!recorded) {
    return (
      <section className="report-wording" data-testid={props.testId ?? 'item-report-wording'} data-status={item.status} data-source="none">
        <p className="card-note" data-testid="item-report-sentence-none">
          {item.status === 'notChecked' ? t('wording.pendingStatus') : t('item.reportWordingNone')}
        </p>
      </section>
    );
  }

  const englishBox = (
    <>
      <SectionEyebrow>{t('wording.en')}</SectionEyebrow>
      {/* 2026-10-28 (device walkthrough B3): grows with the sentence instead of clipping it behind an inner scroll */}
      <TextArea id={`report-sentence-en-${props.item.id}`} value={enText.value} onChange={enText.setValue} rows={3} disabled={locked} testId="item-report-sentence" autoGrow />
      <TextAssist value={enText.value} onChange={enText.setValue} fieldId={`report-sentence-en-${props.item.id}`} lang="en-US" disabled={locked} testId="item-report-sentence-assist" />
      {zhFirst ? null : <p className="card-note">{t('wording.editHint')}</p>}
      {unsynced === 'en' ? (
        <p className="card-note report-wording-stale" data-testid="item-report-en-stale">
          {t('wording.enStale')}
        </p>
      ) : null}
    </>
  );

  const chineseBox = zhFirst ? (
    <>
      <SectionEyebrow>{t('wording.zhEditable')}</SectionEyebrow>
      <TextArea id={`report-sentence-zh-${props.item.id}`} value={zhText.value} onChange={zhText.setValue} rows={3} disabled={locked} testId="item-report-sentence-zh-input" autoGrow />
      <TextAssist value={zhText.value} onChange={zhText.setValue} fieldId={`report-sentence-zh-${props.item.id}`} lang="zh-CN" disabled={locked} testId="item-report-sentence-zh-assist" />
      <p className="card-note">{t('wording.editHintZh')}</p>
      {unsynced === 'zh' ? (
        <p className="card-note report-wording-stale" data-testid="item-report-zh-stale">
          {t('wording.zhStale')}
        </p>
      ) : null}
    </>
  ) : (
    <>
      <SectionEyebrow>{t('wording.zh')}</SectionEyebrow>
      <p className="card-note report-wording-zh" lang="zh-CN" data-testid="item-report-sentence-zh" data-stale={unsynced === 'zh' ? 'true' : 'false'}>
        {zh}
      </p>
      {unsynced === 'zh' ? (
        <p className="card-note report-wording-stale" data-testid="item-report-zh-stale">
          {t('wording.zhStale')}
        </p>
      ) : null}
    </>
  );

  const mode: WriteMode = source === 'inspector' ? 'fromWording' : 'default';

  return (
    <section className="report-wording" data-testid={props.testId ?? 'item-report-wording'} data-status={item.status} data-source={source} data-unsynced={unsynced ?? 'none'}>
      <p className="card-note" data-testid="item-report-source">
        {t(source === 'ai' ? 'wording.source.ai' : source === 'inspector' ? 'wording.source.inspector' : 'wording.source.library')}
      </p>
      {zhFirst ? chineseBox : englishBox}
      {factsChanged ? <InlineMessage kind="info" text={t('wording.factsChanged')} /> : null}
      {zhFirst ? englishBox : chineseBox}
      <SectionEyebrow>{t('wording.trade')}</SectionEyebrow>
      <p className="card-body" data-testid="item-report-trade">
        {trade ? tradeLabel(t, trade) : t('wording.tradeNone')}
      </p>
      {!locked ? (
        <div className="report-wording-ai">
          <Button variant="tonal" icon="psychology" small onClick={() => void requestWrite(mode)} disabled={busy || !online || policyBlock !== null} testId="item-report-write">
            {busy ? t('wording.writing') : t(source === 'inspector' ? 'wording.regenerateFromMine' : source === 'library' ? 'wording.write' : 'wording.rewrite')}
          </Button>
          {source === 'inspector' ? <p className="card-note">{t('wording.regenerateHint')}</p> : null}
          {!online ? (
            <p className="card-note" data-testid="item-report-offline">
              {t('ai.needsOnline')}
            </p>
          ) : null}
          {policyBlock !== null ? (
            <p className="card-note" data-testid="item-report-blocked">
              {aiFailureMessage(t, policyBlock)}
            </p>
          ) : null}
          {error ? <InlineMessage kind="err" text={error} /> : null}
        </div>
      ) : null}
      {confirm !== null ? (
        <ConfirmSheet
          title={t(confirm === 'fromWording' ? 'wording.regenerateTitle' : 'wording.overwriteTitle')}
          body={t(confirm === 'fromWording' ? 'wording.regenerateBody' : 'wording.overwriteBody')}
          confirmLabel={t(confirm === 'fromWording' ? 'wording.regenerateFromMine' : confirm === 'fromObservation' ? 'wording.fromObservation' : 'wording.rewrite')}
          onConfirm={() => {
            const chosen = confirm;
            setConfirm(null);
            void write(chosen);
          }}
          onCancel={() => setConfirm(null)}
          testId="wording-overwrite-confirm"
        />
      ) : null}
    </section>
  );
}
